import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { IGhgEmissionReport } from 'models/ghg-emission-report';
import { emission, percent, TCO2E } from './format';
import GhgComparisonBar from './GhgComparisonBar';
import GhgMetricCard from './GhgMetricCard';
import GhgScenarioFuelTable from './GhgScenarioFuelTable';

interface GhgScenariosProps {
    report: IGhgEmissionReport;
}

export default function GhgScenarios(props: GhgScenariosProps) {
    const report = props.report;
    const hvo = report.hvoScenario;
    const electric = report.electricScenario;

    // The HVO scenario is dropped when no diesel was consumed, so electrification moves up to number 1.
    const electricNumber = report.showHvoScenario ? 2 : 1;

    const [hvoDataVisible, setHvoDataVisible] = useState(false);
    const [electricDataVisible, setElectricDataVisible] = useState(false);

    return (
        <>
            <h3 className="u-fs-18 font-weight-bold mb-2">Kuidas KHG-heidet täiendavalt vähendada?</h3>
            <p className="u-fs-14 ghg-muted mb-4">
                Perioodi tegelik KHG-heide {emission(report.actualEmission)} {TCO2E}
                {' | '}juba välditud {emission(report.avoidedEmission)} {TCO2E}
                {' '}({percent(report.reductionPct)}%)
            </p>

            {report.showHvoScenario ? (
                <div className="ghg-scenario">
                    <div className="ghg-scenario__head">
                        <span className="ghg-scenario__num">1</span>
                        <div>
                            <h4 className="u-fs-16 font-weight-bold mb-1">Diislikütuse asendamine HVO-ga</h4>
                            <p className="u-fs-14 ghg-muted mb-0">
                                Praeguse diislikütuse kasutuse asendamine HVO ReDieseliga.
                            </p>
                        </div>
                    </div>

                    <div className="ghg-metrics ghg-metrics--four">
                        <GhgMetricCard
                            label="Prognoositav heide"
                            value={emission(hvo.prognosisEmission)}
                            unit={TCO2E}
                        />
                        <GhgMetricCard
                            label="Täiendav vähenemine"
                            value={emission(hvo.additionalReduction)}
                            unit={TCO2E}
                        />
                        <GhgMetricCard
                            label="Välditud heide kokku"
                            value={emission(hvo.avoidedTotal)}
                            unit={TCO2E}
                        />
                        <GhgMetricCard
                            label="Kogu vähenemine"
                            value={percent(hvo.reductionPct) + '%'}
                            highlight
                        />
                    </div>

                    <p className="ghg-refbase">
                        Fossiilne võrdlusbaas {emission(report.fossilReference)} {TCO2E}
                    </p>
                    <GhgComparisonBar
                        actualShare={hvo.prognosisShare}
                        reductionPct={hvo.reductionPct}
                        actualLabel="Prognoositav KHG-heide"
                    />

                    <Link to="/esileht" className="ghg-cta">
                        Sulle kehtivat HVO hinda näed Terminali iseteeninduse esilehel
                    </Link>

                    <button
                        type="button"
                        className="ghg-scenario__toggle"
                        onClick={() => setHvoDataVisible(!hvoDataVisible)}
                    >
                        {hvoDataVisible ? 'Peida tarbimisandmed' : 'Vaata tarbimisandmeid'}
                    </button>

                    {hvoDataVisible ? (
                        <GhgScenarioFuelTable
                            title="Tarbimisandmed HVO-stsenaariumi korral"
                            report={report}
                            scenario={hvo}
                            prognosisOf={fuel => fuel.hvoPrognosis}
                        />
                    ) : null}
                </div>
            ) : null}

            <div className="ghg-scenario">
                <div className="ghg-scenario__head">
                    <span className="ghg-scenario__num">{electricNumber}</span>
                    <div>
                        <h4 className="u-fs-16 font-weight-bold mb-1">Sõidukipargi täielik elektrifitseerimine</h4>
                        <p className="u-fs-14 ghg-muted mb-0">
                            Illustratiivne maksimumstsenaarium 100% taastuvelektri kasutamisel.
                        </p>
                    </div>
                </div>

                <div className="ghg-metrics ghg-metrics--four">
                    <GhgMetricCard
                        label="Prognoositav heide"
                        value={emission(electric.prognosisEmission)}
                        unit={TCO2E}
                    />
                    <GhgMetricCard
                        label="Täiendav vähenemine"
                        value={emission(electric.additionalReduction)}
                        unit={TCO2E}
                    />
                    <GhgMetricCard
                        label="Välditud heide kokku"
                        value={emission(electric.avoidedTotal)}
                        unit={TCO2E}
                    />
                    <GhgMetricCard
                        label="Kogu vähenemine"
                        value={percent(electric.reductionPct) + '%'}
                        highlight
                    />
                </div>

                <p className="ghg-refbase">
                    Fossiilne võrdlusbaas {emission(report.fossilReference)} {TCO2E}
                </p>
                <GhgComparisonBar
                    actualShare={electric.prognosisShare}
                    reductionPct={electric.reductionPct}
                    actualLabel="Prognoositav KHG-heide"
                />

                <a
                    className="ghg-cta"
                    href="https://terminalenergia.ee/elekter/autopargi-laadimine/"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    Tutvu Terminali laadimislahendustega veebis ja küsi pakkumist
                </a>

                <button
                    type="button"
                    className="ghg-scenario__toggle"
                    onClick={() => setElectricDataVisible(!electricDataVisible)}
                >
                    {electricDataVisible ? 'Peida tarbimisandmed' : 'Vaata tarbimisandmeid'}
                </button>

                {electricDataVisible ? (
                    <GhgScenarioFuelTable
                        title="Tarbimisandmed elektrifitseerimise stsenaariumi korral"
                        report={report}
                        scenario={electric}
                        prognosisOf={fuel => fuel.electricPrognosis}
                    />
                ) : null}
            </div>

            <div className="ghg-footnotes">
                <p>
                    Stsenaariumid on illustratiivsed. Tegelik tulemus sõltub kasutusele võetava kütuse,
                    sõidukipargi ja elektri päritolu andmetest.
                </p>
                <p className="mb-0">
                    Elektrifitseerimise prognoositav nullheide eeldab Terminali laadimisvõrgus 100%
                    taastuvelektri kasutamist.
                </p>
            </div>
        </>
    );
}
